import React, { useState } from 'react';

import { makeStyles, Theme } from '@material-ui/core';
import axios from 'axios';
import * as Raven from 'raven-js';
import { useForm } from 'react-hook-form';
import { hashHistory } from 'react-router';

import { Track } from '../../../weborder/analytics';
import * as Api from '../../../weborder/Api';
import * as Store from '../../../weborder/Store';
import { Utilities } from '../../../weborder/Utilities';
import { Button, Checkbox, TextField, Typography } from '../../atoms';
import { TextFieldStatusType } from '../../atoms/TextField/TextField';
import { Dialog } from '../../molecules';

const useStyles = makeStyles((theme: Theme) => ({
  loginDialog: {
    '& .MuiTypography-h1': {
      marginBottom: theme.spacing(0.5),
    },

    '& .MuiTypography-body1': {
      color: theme.palette.grey[800],
      marginBottom: theme.spacing(2),

      [theme.breakpoints.up('sm')]: {
        marginBottom: theme.spacing(2.5),
      },
    },
  },
}));

interface FormInput {
  phoneInput: string;
}

type OuterProps = {
  configData: Api.AppConfig;
  enableRememberMe: boolean;
  languageCode: Store.Language['code'];
  lastRestaurantUrl: string;
  params: any;
  phoneNumberConfig: Api.PhoneNumberConfig;
  rememberMe: boolean;
  setRememberMe: any;
  showDialog: boolean;
  translate: any;
};

type Props = OuterProps;

export function LoginDialog(props: Props): JSX.Element {
  const classes = useStyles();
  const {
    configData,
    enableRememberMe,
    languageCode,
    lastRestaurantUrl,
    params,
    phoneNumberConfig,
    rememberMe,
    setRememberMe,
    translate,
  } = props;

  const { register, handleSubmit, setValue, setError, errors } = useForm<
    FormInput
  >({
    mode: 'onChange',
  });

  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const getCodeUrl = (phoneNumber: string): string => {
    let base = `/code/${phoneNumber}`;

    if (params.dlid) {
      base += `/${params.dlid}`;
    }

    return base;
  };

  const isValidPhoneNumber = (textFieldValue): any => {
    const phoneNumber = textFieldValue.replace(/\s/g, '');

    const hasPlusPrefix =
      phoneNumber && phoneNumber.charAt(0) && phoneNumber.charAt(0) == '+';
    const newPhoneNumber =
      (hasPlusPrefix ? '+' : '') + phoneNumber.replace(/\D/g, '');

    const isNumber = newPhoneNumber.match(/^\+?\d*$/);
    setValue('phoneInput', newPhoneNumber);

    if (!isNumber) {
      return;
    }

    const { AutocompletePhoneNumberLength, Prefix } = phoneNumberConfig || {
      AutocompletePhoneNumberLength: 12,
      Prefix: '+1',
    };

    if (
      phoneNumber.length == AutocompletePhoneNumberLength &&
      phoneNumber.startsWith(Prefix) &&
      !hasPlusPrefix &&
      !isFormSubmitted
    ) {
      setIsFormSubmitted(true);
      handleSubmit(onSubmit)();
    }
    return true;
  };

  const toggleRememberMe = () => setRememberMe(!rememberMe);

  const onSubmit = (data: FormInput) => {
    const logError = (error: any) => {
      const dataLog: any = {};

      dataLog.AppNameId = configData.AppNameId;
      dataLog.WhiteLabelId = configData.WhiteLabelId;

      Raven.captureMessage('Submit phone number failed', {
        tags: { category: 'API failure' },
        extra: {
          appconfig: dataLog,
          error: Utilities.handleError(error),
          phone: data.phoneInput,
        },
      });
    };

    setIsFormSubmitted(true);

    const commonHeaders = { ...axios.defaults.headers.common };
    delete commonHeaders['X-Flipdish-Token'];

    const payload = {
      DeviceName: window.navigator.userAgent,
      PhoneNumber: data.phoneInput,
    };
    const options = {
      headers: {
        common: commonHeaders,
      },
      withCredentials: false,
    };

    Track('Login', 'Submit Phone Number Button');

    axios
      .post('/Account/RequestPhoneLoginCodeSms', payload, options)
      .then((response) => {
        const result: Api.Result<any> = response.data;
        if (result.Success !== true) {
          logError(result);
          setError(
            'phoneInput',
            'error',
            translate('Login_LoginDialog_helperTestErrorMessage'),
          );
        } else {
          hashHistory.push(getCodeUrl(data.phoneInput));
        }
      })
      .catch((error: any) => {
        logError(error);
      });
  };

  // const phoneNumberValidation = (): Data.ValidationState => {
  //   const { AutocompletePhoneNumberLength, Prefix } = this.props
  //     .PhoneNumberConfig || {
  //     AutocompletePhoneNumberLength: 12,
  //     Prefix: '+1',
  //   };
  //   const { PhoneNumber } = this.state;

  //   if (PhoneNumber.length < AutocompletePhoneNumberLength) {
  //     return undefined;
  //   }

  //   if (
  //     Utilities.startsWith(PhoneNumber, Prefix) &&
  //     PhoneNumber.length == AutocompletePhoneNumberLength
  //   ) {
  //     return 'success';
  //   }

  //   return 'warning';
  // };

  const renderTermsOfService = (configData): JSX.Element[] => {
    const urlLanguageCode = languageCode ? `${languageCode}/` : '';
    const linkUrl = `https://www.flipdish.com/${urlLanguageCode}flipdish-consumer-terms-and-conditions/`;

    const termsAndConditionsLinks = [
      {
        key: 'Terms_and_conditions',
        linkUrl: configData.TermsAndConditionsUrl,
      },
      {
        key: 'Terms_and_conditions',
        linkUrl: linkUrl,
      },
      {
        key: 'Privacy_policy',
        linkUrl: configData.PrivacyPolicyUrl,
      },
    ];

    const termsAndConditions = termsAndConditionsLinks.map((item, index) => (
      <span key={`${item.key}_${index}`}>
        <a href={item.linkUrl} rel="noopener noreferrer" target="_blank">
          {translate(item.key)}
        </a>
      </span>
    ));

    return termsAndConditions;
  };

  const renderServiceTerms = () => {
    const termsAndConditions = renderTermsOfService(configData);
    const serviceTerms = translate('Login_LoginDialog_ServiceTerms')
      .split('%@')
      .reduce((acc, subString, index) => {
        acc.push(subString);

        if (index < termsAndConditions.length) {
          acc.push(termsAndConditions[index]);
        }

        return acc;
      }, []);

    return serviceTerms;
  };

  const phoneInputAriaLabel = `${translate(
    'Enter_your_mobile_number',
  )} ${translate('Mobile_verification')}`;

  const defaultPhoneNumber = phoneNumberConfig
    ? phoneNumberConfig.Prefix
    : '+1';

  const dialogActions = () => {
    return (
      <Button
        data-fd="phone-submit"
        fullWidth
        key="submit-button"
        onClick={handleSubmit(onSubmit)}
      >
        {translate('Login_LoginDialog_Continue')}
      </Button>
    );
  };

  const getRememberMeText = () => {
    if (enableRememberMe) {
      return translate('Login_LoginDialog_RememberMeFunctionalCookies');
    }
  };

  const handleOnBackLink = () => {
    hashHistory.push(lastRestaurantUrl);
  };

  const fieldHelperText =
    errors.phoneInput && (errors.phoneInput.message as string);
  const fieldStatus = errors.phoneInput
    ? (errors.phoneInput.type as TextFieldStatusType)
    : 'default';

  return (
    <Dialog
      actions={dialogActions()}
      className={classes.loginDialog}
      onTitleClick={handleOnBackLink}
      open={true}
    >
      <Typography variant="h1">
        {translate('Login_LoginDialog_MobileNumber')}
      </Typography>
      <Typography>{translate('Login_LoginDialog_SmsMessage')}</Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        <TextField
          aria-label={phoneInputAriaLabel}
          autoFocus={true}
          data-fd="phone-input"
          defaultValue={defaultPhoneNumber}
          helperText={fieldHelperText}
          id="phoneInput"
          inputRef={register({
            required: false,
            validate: isValidPhoneNumber,
          })}
          status={fieldStatus}
          key="phoneInput"
          name="phoneInput"
          placeholder={'e.g. +44775555555'}
          type="tel"
        />

        {enableRememberMe && (
          <Checkbox
            checked={rememberMe}
            data-fd="rememberMe"
            id="remember_me"
            label={translate('remember_me')}
            onChange={toggleRememberMe}
          />
        )}
      </form>

      <Typography variant="caption">
        {renderServiceTerms()} {getRememberMeText()}
      </Typography>
    </Dialog>
  );
}
